import { LinearProgress, Link, useRecordContext } from "react-admin";
import {
  type AutovioCalendarEvent,
  isASFCourseSession,
  isDrivingLesson,
  isRemoteLesson,
  isTheoryLesson,
} from "../model/autovioCalendarEvents";
import { type SxProps, Tooltip, Typography } from "@mui/material";
import { renderAddress } from "../misc/AddressDisplay";
import { type Course, isCourse } from "../providers/coursesProvider";
import { normalizeStreet } from "../backoffice.utils";
import type { DrivingSchool } from "../providers/drivingSchoolsProvider";
import { drivingSchoolsProvider } from "../providers/drivingSchoolsProvider";

/**
 * Works for `AutovioCalendarEvent` records as well as for `Course` records.
 */
export function LocationField(props: { label?: string; sx?: SxProps }) {
  const { sx } = props;
  const record = useRecordContext<AutovioCalendarEvent | Course>();
  if (!record) {
    return <LinearProgress />;
  }
  const location = getLocation(record);
  if (typeof location === "string") {
    return (
      <Typography variant="body2" sx={sx}>
        {location}
      </Typography>
    );
  } else {
    return (
      <div style={{ display: "flex" }}>
        <Tooltip title={renderAddress(location.postalAddress)} placement="top" arrow>
          <div>
            <Link to={`/branches/${location.id}`} sx={sx ?? { color: "inherit" }}>
              {location.name}
            </Link>
          </div>
        </Tooltip>
      </div>
    );
  }
}

type Branch = DrivingSchool["branches"][number];

export function renderLocation(record: AutovioCalendarEvent | Course): string {
  return getLocation(record, { oneLine: true });
}

function getLocation(record: AutovioCalendarEvent | Course, options: { oneLine: true }): string;
function getLocation(record: AutovioCalendarEvent | Course): string | Branch;
function getLocation(record: AutovioCalendarEvent | Course, options?: { oneLine?: boolean }) {
  if (isRemoteLesson(record)) {
    return "Online";
  }

  const location = isCourse(record)
    ? record.location
    : isTheoryLesson(record) || isASFCourseSession(record)
      ? record.location
      : isDrivingLesson(record)
        ? record.startLocation
        : null;

  if (!location) {
    return "";
  }

  if (location.type === "OnlineMeeting") {
    return "Online";
  }

  const drivingSchool = drivingSchoolsProvider.getOneFromCache(record.drivingSchoolId);
  if (drivingSchool) {
    const normalizedStreet = normalizeStreet(location.street);
    for (const branch of drivingSchool.branches) {
      const branchAddress = branch.postalAddress;
      if (normalizedStreet === branchAddress.normalizedStreet && location.postalCode === branchAddress.postalCode) {
        return options?.oneLine ? branch.name : branch;
      }
    }
  }

  return renderAddress(location, { oneLine: true });
}
